import React, { useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";
import { Dialog } from "primereact/dialog";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { ProgressSpinner } from "primereact/progressspinner";
import VersionDialog from "./VersionDetails";
import { debounce } from "lodash";
import Swal from "sweetalert2";
import { useQuery } from "@tanstack/react-query";

// Constants
const DEBOUNCE_DELAY = 300;
const DEFAULT_ROWS_PER_PAGE = 20;
const ROWS_PER_PAGE_OPTIONS = [20, 50, 100, 200];

// Partner color mapping
const PARTNER_COLORS = {
  "X-Chem": "bg-blue-100",
  HitGen: "bg-green-100",
  UNC: "bg-yellow-100",
  External: "bg-purple-100",
  SGC: "bg-teal-100",
  default: "bg-gray-100",
};

// Utility functions
const showErrorAlert = (title, text, callback) => {
  Swal.fire({
    title,
    text,
    icon: "error",
    confirmButtonText: "OK",
    allowOutsideClick: false,
    customClass: {
      confirmButton:
        "bg-blue-500 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded",
    },
  }).then((result) => {
    if (result.isConfirmed && callback) {
      callback();
    }
  });
};

const navigateToDatasets = () => {
  window.location.href = "/datasets";
};

// Data Dictionary Item Component
const DictItem = React.memo(({ name, entry }) => (
  <ul
    className="p-3 sm:p-4 flex items-center justify-between text-gray-800 border border-gray-300 rounded-md shadow-sm hover:shadow-md transition-shadow duration-200 mb-2"
    key={name}
  >
    <li className="w-1/3 font-medium text-left px-2">{name}</li>
    <li className="w-1/3 text-left px-2">{entry.mode}</li>
    <li className="w-1/3 text-left px-2 text-gray-600">{entry.description}</li>
  </ul>
));

// Custom hooks
const useAsmsData = () => {
  return useQuery({
    queryKey: ["asmsData"],
    queryFn: async () => {
      const response = await fetch("/api/data/asms_dataset");
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });
};

const useFilteredData = (data, searchQuery) => {
  const [filteredData, setFilteredData] = useState([]);

  const debouncedSearch = useMemo(
    () =>
      debounce((term) => {
        if (!data) return;

        if (!term?.trim()) {
          setFilteredData(data);
          return;
        }

        const lowerTerm = term.toLowerCase().trim();
        const results = data.filter(
          (item) =>
            item.target?.toLowerCase().includes(lowerTerm) ||
            item.description?.toLowerCase().includes(lowerTerm) ||
            item.partner?.toLowerCase().includes(lowerTerm)
        );

        setFilteredData(results);
      }, DEBOUNCE_DELAY),
    [data]
  );

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  return filteredData;
};

// Main component
const AsmsTable = React.memo(({ dict, searchQuery }) => {
  // State management
  const [dictDialogInfo, setDictDialogInfo] = useState({
    visible: false,
    partner: null,
  });
  const [visibleConfirm, setVisibleConfirm] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [downloading, setDownloading] = useState({});
  const [selectedOption, setSelectedOption] = useState("Version 1");

  // Data fetching
  const { isPending, error, data } = useAsmsData();
  const filteredData = useFilteredData(data, searchQuery);

  // Error handling
  useEffect(() => {
    if (error) {
      showErrorAlert(
        "Error Loading Data",
        "Something went wrong while fetching the dataset. Please try again later.",
        navigateToDatasets
      );
    }
  }, [error]);

  // Download functionality
  const handleDownload = useCallback(async (datasetId, partner) => {
    if (!datasetId || !partner) {
      console.error("Missing datasetId or partner");
      return;
    }

    try {
      setDownloading((prev) => ({ ...prev, [datasetId]: true }));

      const response = await axios.get(`/api/gcp/getsignedurl`, {
        params: { datasetId, partner },
        timeout: 30000, // 30 second timeout
      });

      const checkResponse = await fetch(
        `/api/gcp/check_file?url=${encodeURIComponent(response.data.signedUrl)}`
      );
      const checkData = await checkResponse.json();

      if (checkData.exists) {
        window.location.assign(response.data.signedUrl);
      } else {
        showErrorAlert(
          "File Not Found",
          "The requested file could not be found. Please try again later.",
          navigateToDatasets
        );
      }
    } catch (error) {
      console.error("Download error:", error);

      let errorMessage = "Error fetching download link. Please try again.";

      if (error.response?.status === 429) {
        errorMessage =
          error.response.data.message ||
          "Too many requests, please try again later.";
      } else if (error.code === "ECONNABORTED") {
        errorMessage = "Request timeout. Please try again.";
      } else if (error.request) {
        errorMessage = "No response from the server. Please try again later.";
      }

      alert(errorMessage);
    } finally {
      setDownloading((prev) => ({ ...prev, [datasetId]: false }));
    }
  }, []);

  // Event handlers
  const handleYesClick = useCallback(() => {
    setVisibleConfirm(false);
    if (selectedRowData?.link && selectedRowData?.partner) {
      handleDownload(selectedRowData.link, selectedRowData.partner);
    }
  }, [handleDownload, selectedRowData]);

  const handleHistoryClick = useCallback((rowData) => {
    setSelectedRow(rowData);
    setHistoryDialog(true);
  }, []);

  const handleDownloadClick = useCallback((rowData) => {
    setSelectedRowData(rowData);
    setVisibleConfirm(true);
  }, []);

  const closeDictDialog = useCallback(() => {
    setDictDialogInfo({ visible: false, partner: null });
  }, []);

  const closeHistoryDialog = useCallback(() => {
    setHistoryDialog(false);
    setSelectedRowData(null);
  }, []);

  // Render functions
  const renderDataDictionaryButton = useCallback(
    () => (
      <button
        className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100"
        onClick={() => setDictDialogInfo({ visible: true, partner: "ASMS" })}
        aria-label="View data dictionary"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504-1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
          />
        </svg>
      </button>
    ),
    []
  );

  const renderHistory = useCallback(
    (rowData) => {
      const isApprovedForDownload = true; // Consider making this dynamic

      if (isApprovedForDownload) {
        return (
          <button
            className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100"
            onClick={() => handleHistoryClick(rowData)}
            aria-label="View dataset history"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
              />
            </svg>
          </button>
        );
      }

      return (
        <Button
          type="button"
          label="Not Authorized"
          tooltip="You need to apply for access to this dataset. Once accepted, you will be able to view it."
          tooltipOptions={{
            position: "bottom",
            mouseTrack: true,
            mouseTrackTop: 15,
          }}
          disabled
        />
      );
    },
    [handleHistoryClick]
  );

  const renderDownloadButton = useCallback(
    (rowData) => {
      const isDownloading = downloading[rowData.link];

      return (
        <button
          className="text-blue-700 py-2 hover:text-blue-500 hover:scale-105 transition duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => handleDownloadClick(rowData)}
          disabled={isDownloading}
          aria-label={isDownloading ? "Downloading..." : "Download dataset"}
        >
          {isDownloading ? (
            <ProgressSpinner style={{ width: "24px", height: "24px" }} />
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9.75v6.75m0 0-3-3m3 3 3-3m-8.25 6a4.5 4.5 0 0 1-1.41-8.775 5.25 5.25 0 0 1 10.233-2.33 3 3 0 0 1 3.758 3.848A3.752 3.752 0 0 1 18 19.5H6.75Z"
              />
            </svg>
          )}
        </button>
      );
    },
    [downloading, handleDownloadClick]
  );

  // const getRowClassName = useCallback((data) => {
  //   // Handle both array and object formats
  //   const partner = Array.isArray(data) ? data[0]?.partner : data?.partner;

  //   return PARTNER_COLORS[partner] || PARTNER_COLORS.default;
  // }, []);

  const getRowClassName = useCallback((data) => {
    // Handle both array and object formats
    const partner = Array.isArray(data) ? data[0]?.partner : data?.partner;

    return PARTNER_COLORS[partner] || PARTNER_COLORS.default;
  }, []);

  // Render sorted dictionary entries
  const renderDictionaryEntries = useMemo(() => {
    if (!dictDialogInfo.visible || !dict[dictDialogInfo.partner]) {
      return null;
    }

    return Object.entries(dict[dictDialogInfo.partner])
      .filter(([key]) => key !== "id")
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, entry]) => (
        <DictItem key={name} name={name} entry={entry} />
      ));
  }, [dict, dictDialogInfo]);

  // Loading state
  if (isPending) {
    return (
      <div className="flex justify-center items-center h-64">
        <ProgressSpinner
          style={{ width: "250px", height: "250px" }}
          strokeWidth="6"
        />
      </div>
    );
  }

  // Error state is handled by useEffect, return null to prevent rendering
  if (error) return null;

  return (
    <div className="w-full bg-white">
      <div className="container mx-auto pl-4 pr-4 md:pr-0 md:pl-0 md:pr-8">
        {/* Download Confirmation Dialog */}
        <Dialog
          visible={visibleConfirm}
          header="Download Dataset"
          style={{ width: "700px" }}
          onHide={() => setVisibleConfirm(false)}
          footer={
            <div className="flex justify-end gap-2">
              <Button
                label="Cancel"
                onClick={() => setVisibleConfirm(false)}
                className="w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-red-500 text-base text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
              />
              <Button
                label="Accept & Download"
                onClick={handleYesClick}
                className="mt-3 w-full inline-flex justify-center rounded-md shadow-sm px-4 py-2 bg-green-500 text-base text-white hover:bg-green-700 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              />
            </div>
          }
        >
          {selectedRowData && (
            <div className="space-y-4">
              <p className="text-gray-700">
                You are about to download:{" "}
                <strong>{selectedRowData.target}</strong>
              </p>
              <p className="text-sm text-gray-600">
                By clicking <strong>Accept & Download</strong>, you agree to use
                this data under the terms of the{" "}
                <a
                  href="https://creativecommons.org/licenses/by/4.0/deed.en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 underline hover:text-blue-700"
                >
                  Creative Commons Attribution 4.0 International License (CC BY
                  4.0)
                </a>
              </p>
            </div>
          )}
        </Dialog>

        {/* Data Table */}
        <DataTable
          className="centered-header"
          value={filteredData}
          paginator
          rows={DEFAULT_ROWS_PER_PAGE}
          rowClassName={getRowClassName}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          emptyMessage="No datasets found matching your search criteria."
          loading={isPending}
        >
          <Column
            field="target"
            header="Target Name"
            style={{ width: "10%" }}
            alignHeader="center"
            sortable
          />
          <Column
            field="description"
            header="Description"
            style={{ width: "20%" }}
            alignHeader="center"
          />
          <Column
            field="selectionDate"
            header="Selection Date"
            style={{ width: "15%" }}
            alignHeader="center"
            sortable
          />
          <Column
            field="partner"
            header="Partner"
            style={{ width: "12.5%" }}
            alignHeader="center"
            sortable
          />
          <Column
            body={renderDataDictionaryButton}
            style={{ width: "12.5%" }}
            alignHeader="center"
            header="Data Dictionary"
          />
          <Column
            body={renderDownloadButton}
            style={{ width: "15%" }}
            alignHeader="center"
            header="Download Dataset"
          />
          <Column
            body={renderHistory}
            style={{ width: "15%" }}
            alignHeader="center"
            header="Dataset History"
          />
        </DataTable>

        {/* Data Dictionary Dialog */}
        <Dialog
          visible={dictDialogInfo.visible}
          onHide={closeDictDialog}
          header="Data Dictionary"
          style={{ width: "80vw", maxWidth: "900px" }}
          maximizable
        >
          <div className="space-y-2">
            <ul className="bg-gray-200 p-2 sm:p-3 flex items-center text-gray-800 border border-gray-400 rounded">
              <li className="w-1/3 text-left font-bold px-2">Field Name</li>
              <li className="w-1/3 text-left font-bold px-2">Data Type</li>
              <li className="w-1/3 text-left font-bold px-2">Description</li>
            </ul>
            {renderDictionaryEntries}
          </div>
        </Dialog>

        {/* Version History Dialog */}
        <VersionDialog
          header="Dataset History"
          visible={historyDialog}
          maximizable
          style={{ width: "40vw" }}
          onHide={closeHistoryDialog}
          rowData={selectedRow}
          dataDictionary={dict}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
        />
      </div>
    </div>
  );
});

AsmsTable.displayName = "AsmsTable";

export { AsmsTable };
